import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/material';
import { UseMutateAsyncFunction, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import FormProvider, { RHFTextField, RHFAutocomplete } from '@/commons/hook-form';
import RHFDateField, { TypeDateField } from '@/commons/hook-form/RHFDateField';
import { getPlans } from '@/services/plans';
import { translates } from '@/utils';
import { Button } from '@PorkyCol/button';
import { AxiosResponse } from 'axios';
import { AutocompleteLabels } from '@modules/orders/form/order-detail/products-items-list';
import { useAuthContext } from '@hooks/useAuthContext';
import dayjs from 'dayjs';
import { Reservations } from '@/services/reservations/types';
import { reservationSchema } from './validation';

type FormValuesProps = any;
type Props = {
  isEdit?: boolean;
  current?: Reservations;
  onSave: UseMutateAsyncFunction<AxiosResponse<any, any>, unknown, unknown, unknown>;
};
export default function ReservationForm({ isEdit, current, onSave }: Props) {
  const { user } = useAuthContext();

  const { data: plans } = useQuery({
    queryKey: ['plans'],
    queryFn: getPlans,
    select: (data: any) =>
      data?.map?.(
        (plan: any) =>
          ({
            label: plan.name,
            value: plan._id,
          })
      )?? [],
  });

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(reservationSchema),
    defaultValues: current,
  } as any);

  const {
    reset,
    handleSubmit,

    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (isEdit && current) {
      reset(current as unknown as Partial<Reservations>);
    }
    if (!isEdit) {
      reset(current as unknown as Partial<Reservations>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, current]);

  const onSubmit = async (temp: FormValuesProps) => {
    try {
      const newValues = {
        reserver: {
          name: temp.name,
          email: temp.email,
          phone: temp.phone,
          document: temp.document,
        },
        createAt: dayjs(temp.createAt).format('YYYY-MM-DD HH:mm:ss'),
        attendees: temp.attendees ?? 0,
        date: dayjs(temp.date).format('YYYY-MM-DD'),
        time: dayjs(temp.time).format('HH:mm'),
        plan: temp?.plan?.value,
        branch: user?.branchSelected?.id,
        message: temp.message,
      };
      await onSave(newValues);
      reset();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} style={{}}>
      <Stack sx={{ p: 1, gap: 2 }}>
        <Stack gap={2} flexDirection="row" flexWrap="wrap">
          <RHFTextField
            sx={{ width: '48%' }}
            fullWidth={false}
            name="name"
            label={translates.name}
          />
          <RHFTextField
            sx={{ width: '48%' }}
            fullWidth={false}
            name="email"
            label={translates.email}
          />
          <RHFTextField
            sx={{ width: '48%' }}
            fullWidth={false}
            name="phone"
            type="number"
            label={translates.phone}
          />
          <RHFTextField
            sx={{ width: '48%' }}
            fullWidth={false}
            type="number"
            name="document"
            label={translates.document}
          />
          <RHFTextField
            sx={{ width: '48%' }}
            fullWidth={false}
            name="attendees"
            type="number"
            label={translates.assistants}
          />
          <RHFAutocomplete
              sx={{ width: '48%' }}
              name="plan"
              options={plans??[]}
              label={translates.plans}
              isOptionEqualToValue={(option: AutocompleteLabels, selected: AutocompleteLabels) =>
                  option.value === selected.value
              }
          />
          <RHFDateField
            required
            sx={{ width: '48%' }}
            name="date"
            label={translates.bookingDate}
            type={TypeDateField.DATE}
          />
          <RHFDateField
            sx={{ width: '48%' }}
            required
            name="time"
            label={translates.time}
            type={TypeDateField.TIME}
          />
          <RHFTextField
              fullWidth
              name="message"
              label={translates.observation}
          />

        </Stack>

        <Stack justifyContent="flex-end" gap={2} flexDirection="row" sx={{ mt: 3 }}>
          <Button type="submit" variant="solid" size="sm" color="success" loading={isSubmitting}>
            {!isEdit ? [`${translates.create} ${translates.reservation}`] : [translates.saveChanges]}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
